import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import lowerCase from 'lodash/lowerCase'
import map from 'lodash/map'
import reduce from 'lodash/reduce'
import sortBy from 'lodash/sortBy'
import startCase from 'lodash/startCase'
import uniq from 'lodash/uniq'

import {
  formatDropdownCustomOptions,
  formatNameWithCode,
} from 'utils/formatters'
import { getDeliveryWindowText } from 'utils/transformations/deliveryWindows'
import { fromGlobalId } from 'utils/transformations/graphql'

import { filterProductTraitValuesByTraitCode } from 'shop/products/utils'

export const TAG_GROUP = 'tagGroup'
export const TAG_NAME = 'tagName'
export const TAG_VALUE = 'tagValue'

export const formatDropdownOption = (
  options,
  format = {
    value: 'id',
    textFunc: (e) => formatNameWithCode(e.name, e.code),
    id: 'id',
    code: 'id',
  },
) => formatDropdownCustomOptions(options, format)

export const groupCategories = (categories, subcategories = []) =>
  map(categories, (category) => {
    const children = subcategories.filter(
      (subcategory) =>
        subcategory.externalIdentifier ===
        fromGlobalId(fromGlobalId(category.id).id).id,
    )
    return {
      ...category,
      children,
    }
  })

export const formatCategories = (categories = [], parentName = '') =>
  sortBy(
    categories.map((category) => {
      const name = startCase(lowerCase(category.value))
      const parentFullName = parentName ? `${parentName} - ${name}` : name
      return {
        id: category.id,
        code: get(category, 'trait.code'),
        label: name,
        text: parentFullName,
        ...(category.children && {
          children: formatCategories(category.children, parentFullName),
        }),
      }
    }),
    ['label'],
  )

export const getCategoryFilter = ({
  genderTraits,
  categoryTraits,
  subcategoryTraits,
}) => {
  const groupedCategories = groupCategories(
    genderTraits,
    groupCategories(categoryTraits, subcategoryTraits),
  )
  return formatCategories(groupedCategories)
}

export const getDeliveryWindowsFromCollections = (
  collections,
  userDateFormat,
) => {
  const processed = {}
  const options = []

  const deliveryWindows = sortBy(
    collections.map(({ delivery }) => delivery),
    (delivery) => delivery.startShipDate,
  )

  deliveryWindows.forEach(({ id, startShipDate, completeShipDate }) => {
    const text = getDeliveryWindowText(
      startShipDate,
      completeShipDate,
      undefined,
      undefined,
      userDateFormat,
    )
    if (!processed[text]) {
      processed[text] = true
      if (startShipDate) {
        options.push({
          value: id,
          id,
          text,
        })
      } else {
        options.unshift({
          value: id,
          id,
          text,
        })
      }
    }
  })

  return options
}

export const formatTraitValuesDropdown = (traitValues, code) =>
  formatDropdownOption(
    filterProductTraitValuesByTraitCode(traitValues, code).map(
      (traitValue) => ({
        ...traitValue,
        value: formatNameWithCode(
          traitValue.value,
          traitValue.externalIdentifier,
        ),
      }),
    ),
    {
      value: 'id',
      textFunc: (e) => e.value,
      id: 'id',
      code: 'trait.code',
    },
  )

export const formatFiltersForTracking = (
  selectedFilters = {},
  filterSetup = [],
) => {
  const formattedFilters = {}
  Object.keys(selectedFilters).forEach((filterKey) => {
    const selectedFilter = selectedFilters[filterKey]
    if (!isEmpty(selectedFilter)) {
      const filter = filterSetup.find((el) => el.key === filterKey)
      formattedFilters[filter.title] =
        selectedFilter.text ||
        selectedFilter.map((value) => value.text).join(', ')
    }
  })
  return formattedFilters
}

export const getTagObject = (styleTags = []) =>
  styleTags.reduce((tagGroups, currentElem) => {
    const currentGroups = { ...tagGroups }
    const {
      value: tagValue,
      tag: { name: tagName, groupName, id: nameId },
    } = currentElem
    currentGroups[groupName] = {
      ...get(currentGroups, groupName, {}),
      [tagName]: {
        id: nameId,
        values: uniq([
          ...get(currentGroups, [groupName, tagName, 'values'], []),
          tagValue,
        ]),
      },
    }
    return currentGroups
  }, {})

// eslint-disable-next-line no-irregular-whitespace
const cleanSpaces = (string) => string.replace(/ /g, ' ')

const toUpperCleanLabel = (e) => e.label.replace(/\s+/g, '').toUpperCase()

export const transformTagObjectToOptions = (tagObject = {}) =>
  sortBy(
    map(tagObject, (groupValue, tagGroup) => {
      const cleanTagGroup = cleanSpaces(tagGroup)
      const children = sortBy(
        map(groupValue, (tagValue, tagName) => {
          const cleanName = cleanSpaces(tagName)
          return {
            label: cleanName,
            value: cleanName,
            text: `${cleanTagGroup} - ${cleanName}`,
            code: TAG_NAME,
            id: tagValue.id,
            children: sortBy(
              reduce(
                tagValue.values,
                (currentChildren, value) => {
                  const cleanValue = cleanSpaces(value)
                  return [
                    ...currentChildren,
                    {
                      label: cleanValue,
                      value: cleanValue,
                      text: `${cleanTagGroup} - ${cleanName} - ${cleanValue}`,
                      code: TAG_VALUE,
                      id: cleanValue,
                    },
                  ]
                },
                [],
              ),
              toUpperCleanLabel,
            ),
          }
        }),
        toUpperCleanLabel,
      )
      return {
        label: cleanTagGroup,
        value: cleanTagGroup,
        text: cleanTagGroup,
        code: TAG_GROUP,
        disabled: true,
        children,
      }
    }),
    toUpperCleanLabel,
  )

export const transformStyleTags = (styleTags) => {
  const styleTagObject = getTagObject(styleTags)
  return transformTagObjectToOptions(styleTagObject)
}
